import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import store from "@/store";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  methodGroupList,
  methodGroupCreate,
  methodGroupDelete,
} from "@/common/api/rest/method/method-group";

import type {
  MethodGroupType,
  MethodGroupPayloadType,
} from "@/common/types/method/method-group";

import { RegisterTheme } from "@/common/themes";
import {
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";
import MethodGroupForm from "@/components/forms/method-group.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
]);

const tableData = ref<MethodGroupType[]>([]);

const payload = ref<MethodGroupPayloadType | null>(null);
const selected = ref<MethodGroupType | null>(null);

const isLoading = ref(false);
const showModalAdd = ref(false);
const showModalDel = ref(false);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const accountId = computed(() => route.params.id as string);

const delMessage = computed(() => {
  const message = t("method.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function fetchData() {
  isLoading.value = true;

  methodGroupList(accountId.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function add() {
  showModalAdd.value = true;
}

function addCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  showModalAdd.value = false;
  store.commit("SET_LOADING", true);

  methodGroupCreate(accountId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(
          `/${accountId.value}/register/method/${data.group_id}/edit`,
        );
      } else {
        toast({ message: message, type: "error" });
        showModalAdd.value = true;
        fetchData();
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
      showModalAdd.value = true;
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: MethodGroupType) {
  router.push(`/${accountId.value}/register/method/${data.group_id}/edit`);
}

function del(data: MethodGroupType) {
  showModalDel.value = true;
  selected.value = data;
}

function delCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!selected.value) return;

  showModalDel.value = false;
  store.commit("SET_LOADING", true);

  methodGroupDelete(accountId.value, selected.value.group_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        selected.value = null;
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

onMounted(() => {
  if (accountId.value) fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "method" }, {
      title: _withCtx(() => [
        _createVNode(_unref(svgIcon), {
          icon: "method",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.method_group")), 1)
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(baseTable), {
          header: tableHeader.value,
          data: tableData.value,
          limit: limit.value,
          page: page.value,
          pages: pages.value,
          total: total.value,
          searched: searched.value,
          options: true,
          edit: true,
          del: true,
          "onChange:page": changePage,
          onSearching: searchItem,
          "onBtn:add": _cache[0] || (_cache[0] = ($event: any) => (add())),
          "onBtn:edit": edit,
          "onBtn:del": del
        }, null, 8, ["header", "data", "limit", "page", "pages", "total", "searched"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseModal), {
      show: showModalAdd.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.confirm'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[2] || (_cache[2] = ($event: any) => (addCancel())),
      onConfirm: _cache[3] || (_cache[3] = ($event: any) => (addConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(MethodGroupForm, {
          title: _unref(t)('common.labels.add', { concat: _unref(t)('common.labels.group') }),
          data: payload.value,
          onUpdateData: _cache[1] || (_cache[1] = ($event: any) => (payload.value = $event))
        }, null, 8, ["title", "data"])
      ]),
      _: 1
    }, 8, ["show", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showModalDel.value,
      title: _unref(t)('method.texts.del_method'),
      message: delMessage.value,
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (delCancel())),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (delConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})